import React, { useRef } from "react";
import { TFunction } from 'i18next';
import { useNavigate } from "react-router-dom";

import { Button } from "../features/swimLane";
import { TypingParagraph } from "../utils";
import Process from "./animation/process";

interface ChildProps {
    refresh: () => void;
    error: boolean;
    t: TFunction;
    time?: number;
}

/** Animation + titre — monté une fois, jamais re-render (stable quand `time` passe 0 → stats). */
const SwimlaneLoadingAnimation = React.memo(function SwimlaneLoadingAnimation({
    t,
}: {
    t: TFunction;
}) {
    return (
        <>
            <Process />
            <div className=" d-flex justify-content-center align-items-center text-white">
                <div className="text-white text-start pb-2 fs-5 d-flex flex-column">
                    <span className="d-flex justify-content-center align-items-center mb-3" />
                    <span>{t('features.swimlane.pleaseWait')}</span>
                </div>
            </div>
        </>
    );
}, () => true);

/** Messages typing — monté quand stats dispo ; figés au 1er render. */
const SwimlaneLoadingMessages = React.memo(function SwimlaneLoadingMessages({
    t,
    time,
}: {
    t: TFunction;
    time: number;
}) {
    const messagesRef = useRef<string[] | null>(null);

    if (!messagesRef.current) {
        messagesRef.current = [
            t('features.swimlane.s1', { time }),
            t('features.swimlane.s2', { time }),
            t('features.swimlane.s3', { time }),
        ];
    }

    return (
        <div className=" d-flex justify-content-center align-items-center text-light opacity-50">
            <TypingParagraph
                messages={messagesRef.current}
                delayBetweenMessages={2000}
                typingSpeed={50}
                className="fs-6"
            />
        </div>
    );
}, (prev, next) => prev.time === next.time);

const SwimlaneLoadingOverlay: React.FC<{ t: TFunction; time: number }> = ({ t, time }) => (
    <div className="mt-5 p-2 ">
        <SwimlaneLoadingAnimation t={t} />
        {time !== 0 && <SwimlaneLoadingMessages t={t} time={time} />}
    </div>
);

const SwimlaneError: React.FC<ChildProps> = (props) => {
    const { error, t, time = 0 } = props;
    const navigate = useNavigate();
    const backToChatBot = () => {
        navigate(-1);
    };

    return (
        <div className="pt-5 h-100 bg-dark m-0 smpage position-fixed w-100 ">
            {error ?
                <div className="card text-center rounded-0 bg-dark border-0">
                    <div className="card-body">
                        <span>
                            <i className="fa-solid fa-triangle-exclamation fs-1 text-white mb-5"></i>
                        </span>
                        <h5 className="card-title text-theme"> {t('features.swimlane.oops')}</h5>
                        <p className="card-text px-5 mx-5 text-white">
                            {t('features.swimlane.WeSorry')}
                        </p>
                        <div className="d-flex justify-content-center">
                            <Button className="dhx_sample-btn dhx_sample-btn--flat border-0 bg-theme text-center " name={t('words.refresh')} onClick={backToChatBot} submit={false} />
                        </div>
                    </div>
                </div>
                :
                <SwimlaneLoadingOverlay t={t} time={time} />
            }
        </div>
    );
};

export default React.memo(SwimlaneError, (prev, next) => {
    if (prev.error !== next.error) return false;
    if (prev.error) return prev.t === next.t;
    if (prev.time === 0 && next.time === 0) return true;
    if (prev.time !== 0 && next.time !== 0) return true;
    return false;
});
